import type { ExternalDesignKnowledgeLibraryGap, LibraryGap } from '@cogo/agentic-generator'
import type { UxApplicationLibraryAdmissionIssue } from '@cogo/kg-browser'
import { STUDIO_NS } from '@/mcp/types'
import { deriveFromSource, type SourceTokenSnapshot } from '../design-system/source-derived/capability'
import type { SourceDerivedDesignSystem } from '../design-system/source-derived/types'
import {
  translateAdvice,
  translateAxisResolution,
  translateMachineGap,
  type ReadinessItem,
} from './translate-gaps'

/**
 * The bus namespaces every tool under `cogo.studio.`, so registration takes
 * the SHORT name while callers and manifests see the full capability id.
 * Both are defined here, one composed from the other
 * (`undo-middleware.ts`'s `${STUDIO_NS}undo` convention), and
 * `register-readiness-tools.ts` registers with the short constant — so the
 * two spellings cannot drift. They used to: the full id lived here with no
 * production consumer at all while the registration carried its own literal.
 */
export const CHECK_GENERATION_READINESS_TOOL = 'generation.checkReadiness'
export const CHECK_GENERATION_READINESS_CAPABILITY =
  `${STUDIO_NS}${CHECK_GENERATION_READINESS_TOOL}` as const

/**
 * 기계 검증이 낼 수 있는 gap 세 어휘의 합집합. 실제 정본:
 * - `LibraryGap` — packages/cogo-agentic-generator/src/library-composition/types.ts:428-433
 * - `ExternalDesignKnowledgeLibraryGap` — packages/cogo-agentic-generator/src/continuous-knowledge/astryx-library-seam.ts:78-83
 * - `UxApplicationLibraryAdmissionIssue` — packages/cogo-kg-browser/src/ux-application-library-admission.ts:37-42
 *
 * 앞의 둘은 `evidenceRefs`를 갖지만 세 번째는 갖지 않는다 — 대신
 * `focusNode?: string`을 갖는다. `toMachineGapCopyInput`이 그 차이를 흡수한다
 * (translate-gaps.ts 상단 주석의 정정을 그대로 따른다).
 */
export type MachineGap = LibraryGap | ExternalDesignKnowledgeLibraryGap | UxApplicationLibraryAdmissionIssue

/**
 * `collectMachineGaps`의 반환 계약. Task 1의 `PreconditionVerdict`(`{ok:true;
 * evidence?} | {ok:false; reason; recovery?}`)와 같은 모양으로 "판정 자체가
 * 수행됐는가"를 1급 값으로 만든다 — 리뷰 라운드 1 Important: 이전 버전은
 * `() => Promise<readonly MachineGap[]>`이라 "점검했고 gaps 가 0개"와 "아직
 * 점검 자체를 못 한다"를 똑같이 빈 배열 하나로 뭉갰다. 그 결과 `blocking
 * Count`가 항상 0인 리포트가 "안전하다고 확인됨"으로 읽혔다(실제로는 "확인한
 * 적이 없음"). `performed:false`는 그 자체로 실패가 아니다 — 아직 프리생성
 * 시점에 물어볼 데이터가 없다는 사실을 정직하게 전달하는 정상 값이다.
 */
export type MachineGapCollection =
  | { performed: true; gaps: readonly MachineGap[] }
  | { performed: false; reason: string }

export interface ReadinessReport {
  status: 'success' | 'error'
  items: ReadinessItem[]
  blockingCount: number
  checkedAt: string
  /**
   * `blockingCount`만으로는 "기계 검증을 돌렸고 깨끗했다"와 "기계 검증을
   * 아예 돌리지 않았다"를 구분할 수 없다 — 이 필드가 그 구분이다.
   * `performed:false`일 때 `blockingCount`는 디자인시스템 축에서만 나올 수
   * 있고, 기계 gap 부재를 "확인됨"으로 주장하지 않는다.
   */
  machineValidation: { performed: boolean; reason?: string }
  designSystem: SourceDerivedDesignSystem | null
}

export interface CheckGenerationReadinessInput {
  projectId: string
  /** 소스를 읽는 책임은 호출자에게 있다 — `capability.ts`(`deriveFromSource`)와 같은 원칙. */
  loadTokens: () => Promise<SourceTokenSnapshot>
  /**
   * 생성을 막는 기계 gap(라이브러리 합성·외부 디자인 지식·UX 응용 라이브러리
   * admission 세 어휘)을 모은다. 실제 배선은 등록기(register-readiness-tools.ts)
   * 가 공급한다 — 이 함수는 그 결과를 순수하게 사용자 언어로 접기만 한다.
   *
   * `MachineGapCollection`(위)을 반환한다 — 세 admission 함수 모두 완성된
   * 생성 후보나 RDF quads를 요구해 프리생성 시점엔 아직 실제로 돌릴 데이터가
   * 없으므로(register-readiness-tools.ts의 옵션 JSDoc 참고), 지금 이 자리의
   * 실제 배선은 `{performed:false, reason: '...'}`을 반환한다 — "돌렸더니
   * 깨끗했다"고 위장하지 않는다.
   */
  collectMachineGaps: () => Promise<MachineGapCollection>
}

function hasEvidenceRefs(gap: MachineGap): gap is LibraryGap | ExternalDesignKnowledgeLibraryGap {
  return 'evidenceRefs' in gap
}

/**
 * `UxApplicationLibraryAdmissionIssue`는 `evidenceRefs`가 없고 `focusNode?:
 * string`만 갖는다 — `focusNode`가 있으면 단일 원소 배열로, 없으면 빈
 * 배열로 적응한다. 나머지 둘은 이미 `evidenceRefs`를 가지므로 그대로 넘긴다.
 */
function toMachineGapCopyInput(gap: MachineGap): {
  code: string
  path: string
  message: string
  evidenceRefs: readonly string[]
} {
  if (hasEvidenceRefs(gap)) return gap
  return {
    code: gap.code,
    path: gap.path,
    message: gap.message,
    evidenceRefs: gap.focusNode ? [gap.focusNode] : [],
  }
}

function blockingCountOf(items: readonly ReadinessItem[]): number {
  return items.filter((item) => item.kind === 'blocking').length
}

/**
 * 점검 자체가 실패했을 때의 접힘. "생성 가능"으로 조용히 폴백하지 않도록
 * `status:'error'`를 내면서도, Task 1(`evaluatePreconditions`)이 세운
 * 선례(내부 오류도 조용히 삼키지 않고 사용자 문장으로 남긴다)를 그대로
 * 따라 blocking 항목 하나에 실패 사유를 담는다 — 이 보고서만 읽는 화면이라도
 * "무엇을 확인할 수 없었는지" 빈손으로 돌아가지 않는다.
 */
function errorReport(checkedAt: string, message: string): ReadinessReport {
  const items: ReadinessItem[] = [
    {
      kind: 'blocking',
      title: '생성 준비 상태를 확인할 수 없습니다',
      explanation: `점검 자체가 실패해 생성 가능 여부를 판단할 수 없습니다: ${message}`,
      evidenceRefs: [],
    },
  ]
  return {
    status: 'error',
    items,
    blockingCount: blockingCountOf(items),
    checkedAt,
    // 점검 자체가 실패했으므로 기계 검증이 수행됐는지조차 알 수 없다 —
    // performed:true 를 주장할 근거가 없다.
    machineValidation: {
      performed: false,
      reason: '점검 자체가 실패해 기계 검증 수행 여부를 확인할 수 없습니다.',
    },
    designSystem: null,
  }
}

/**
 * "생성 버튼을 눌러도 되는가"라는 질문 하나를 접는다: 디자인시스템 6축
 * (from-source/gap/advice, Task 1/2가 소비하는 source-derived 산출) + 기계
 * gap(세 어휘, 수행된 경우에만) → 사용자 언어 `ReadinessItem` 목록 + blocking
 * 집계 하나 + 기계 검증 수행 여부.
 *
 * fail-closed: `loadTokens`나 `collectMachineGaps` 어느 쪽이든 던지면
 * `status:'error'`로 접는다 — 소스가 없는 정상적인 gap 상태(zero-shot 유효)
 * 로 조용히 대체하지 않는다. "소스가 아예 없음"과 "소스가 있는데 못 읽음"의
 * 구분은 이미 `loadCurrentProjectSourceTokens`가 던짐/빈 스냅샷으로 나눠
 * 놓았다 — 이 함수는 그 구분을 다시 하지 않고 오직 "던졌는가"만 본다.
 *
 * `collectMachineGaps`가 던지는 것(예외)과 `{performed:false, reason}`을
 * 반환하는 것(정상 값)은 다른 신호다 — 전자는 예상 밖 고장이라 `status:
 * 'error'`로 접히고, 후자는 "아직 이 채널이 배선되지 않았다"는 정상적인
 * 사실이라 `status:'success'` 유지, 대신 `machineValidation.performed` 가
 * false 로 그 사실을 그대로 실어 나른다 — `blockingCount`가 0이라고 해서
 * "기계 검증까지 깨끗하다"고 넘겨짚지 못하게 한다.
 */
export async function checkGenerationReadiness(
  input: CheckGenerationReadinessInput,
): Promise<ReadinessReport> {
  const checkedAt = new Date().toISOString()
  try {
    const [designSystem, machineGapCollection] = await Promise.all([
      deriveFromSource({ projectId: input.projectId, loadTokens: input.loadTokens }),
      input.collectMachineGaps(),
    ])

    const gapItems = machineGapCollection.performed
      ? machineGapCollection.gaps.map((gap) => translateMachineGap(toMachineGapCopyInput(gap)))
      : []
    const axisItems = Object.values(designSystem.axes).flatMap((axis) => {
      const unfilled = translateAxisResolution(axis)
      const advisory = axis.advice.map((advice) => translateAdvice(axis.axis, advice))
      return unfilled ? [unfilled, ...advisory] : advisory
    })

    const items = [...gapItems, ...axisItems]
    return {
      status: 'success',
      items,
      blockingCount: blockingCountOf(items),
      checkedAt,
      machineValidation: machineGapCollection.performed
        ? { performed: true }
        : { performed: false, reason: machineGapCollection.reason },
      designSystem,
    }
  } catch (error) {
    return errorReport(checkedAt, error instanceof Error ? error.message : String(error))
  }
}
